import { Menu, Position } from '@busybusy/webapp-react-ui';
import { ArrowDownIcon } from 'assets/icons';
import classNames from 'classnames';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import _ from 'lodash';
import { useRef } from 'react';
import './DropDownButton.scss';

export interface IDropDownButtonProps<T> {
  value: T;
  onOptionSelected: (option: T) => void;
  onOptionClicked: (option: T) => void;
  options: Array<IDropDownOption<T>>;
  forwardRef?: (element: HTMLElement) => void;
  className?: string | object;
}

export interface IDropDownOption<T> {
  value: T;
  label: string;
}

function DropDownButton<T>(props: IDropDownButtonProps<T>) {
  const { value, options, onOptionSelected, onOptionClicked, forwardRef, className } = props;

  const wrapperElement = useRef<HTMLDivElement>();

  function renderTrigger(ref: (el: HTMLElement) => void) {
    function setRefs(element: HTMLDivElement) {
      ref(element);

      if (forwardRef) {
        forwardRef(element);
      }
    }

    return (
      <div ref={setRefs} tabIndex={0}>
        <div className="drop-down-arrow">
          <IconButton svg={ArrowDownIcon} />
        </div>
      </div>
    );
  }

  function renderContent(close: () => void) {
    return _.map(options, (option) => {
      function onSelect() {
        onOptionSelected(option.value);
        close();
      }
      return (
        <div className="drop-down-menu-item" onClick={onSelect} key={`${option.value}`}>
          {option.label}
        </div>
      );
    });
  }

  function onOptionClick() {
    onOptionClicked(value);
  }

  function getLabel() {
    const currentOption = _.find(options, (option) => option.value === value);

    if (currentOption) {
      return currentOption.label;
    } else {
      throw Error('Unexpected option found in drop down button');
    }
  }

  function getMenuWidth() {
    if (wrapperElement.current) {
      return _.toString(wrapperElement.current.offsetWidth) + 'px';
    } else {
      return '0px';
    }
  }

  const classes = classNames(
    {
      'drop-down-button': true,
    },
    className
  );

  return (
    <div className={classes} ref={(elt: HTMLDivElement) => (wrapperElement.current = elt)}>
      <div className="selected-option" onClick={onOptionClick}>
        {getLabel()}
      </div>
      <ConditionalContainer condition={options.length > 1}>
        <Menu width={getMenuWidth()} trigger={renderTrigger} content={renderContent} position={Position.BOTTOM_END} />
      </ConditionalContainer>
    </div>
  );
}

export default DropDownButton;
